export interface TraceAuditEventLike {
  eventId: string;
  agentRunId: string;
  timestamp: string;
  eventType: string;
  studioId: string;
  platform: string;
  actor: string;
  traceId: string;
  spanId: string;
  metadata: Record<string, unknown>;
  humanSummary?: string | null;
}

export interface WorkflowProgressPayloadLike {
  type: "agent_workflow_progress";
  eventId: string;
  timestamp: string;
  agentRunId: string;
  workflowType: "langgraph_orchestrator" | "vision_goal_runner";
  stage: "run" | "node" | "step" | "phase";
  status: "started" | "completed" | "failed";
  stepId: string;
  sequence?: number;
  metadata?: Record<string, unknown>;
  studioId: string;
  platform: string;
  actor: "orchestrator" | "vision_agent";
  label?: string;
}

type WorkflowType = WorkflowProgressPayloadLike["workflowType"];

export const ORCHESTRATOR_WORKFLOW_PATH = [
  "loadContext",
  "classifyIntent",
  "setEscalationReason",
  "resolveIdentity",
  "executeTools",
  "retrieveKnowledge",
  "composeResponse",
  "persistDraftProjection",
  "approvalInterrupt",
  "postHitlResumeProjection",
  "sendResponse",
] as const;

export const VISION_WORKFLOW_PATH = [
  "started",
  "goal_decomposition",
  "tier_decision",
  "tier_execution",
  "data_extraction",
  "completed",
] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function getSequenceFromMetadata(metadata: unknown): number | null {
  const md = asRecord(metadata);
  if (!md) return null;
  return toFiniteNumber(md.sequence);
}

function getWorkflowTypeFromMetadata(metadata: unknown): WorkflowType | null {
  const md = asRecord(metadata);
  if (!md) return null;
  const workflowType = md.workflowType;
  if (workflowType === "langgraph_orchestrator" || workflowType === "vision_goal_runner") {
    return workflowType;
  }
  return null;
}

function getStageFromMetadata(
  metadata: unknown,
): WorkflowProgressPayloadLike["stage"] | null {
  const md = asRecord(metadata);
  if (!md) return null;
  const stage = md.stage;
  if (stage === "run" || stage === "node" || stage === "step" || stage === "phase") return stage;
  return null;
}

export function getStatusFromMetadata(
  metadata: unknown,
): WorkflowProgressPayloadLike["status"] | null {
  const md = asRecord(metadata);
  if (!md) return null;
  const status = md.status;
  if (status === "started" || status === "completed" || status === "failed") return status;
  return null;
}

export function getStepIdFromMetadata(metadata: unknown): string | null {
  const md = asRecord(metadata);
  if (!md) return null;
  return typeof md.stepId === "string" && md.stepId.trim() !== "" ? md.stepId : null;
}

export function getAuditHumanSummary(
  event: Pick<TraceAuditEventLike, "humanSummary" | "metadata">,
): string | null {
  if (typeof event.humanSummary === "string" && event.humanSummary.trim() !== "") {
    return event.humanSummary.trim();
  }
  const md = asRecord(event.metadata);
  const fromMeta = md?.humanSummary;
  if (typeof fromMeta === "string" && fromMeta.trim() !== "") return fromMeta.trim();
  return null;
}

/**
 * A deterministic (discovered-API) call reports its outcome in `metadata.phase`, not in the event
 * name — both the attempt and the result are `api_*` events. Without this, a failed HTTP tier
 * rendered as a neutral row next to the browser steps that ran because of it.
 */
export function isFailedApiPhase(event: Pick<TraceAuditEventLike, "eventType" | "metadata">): boolean {
  if (!event.eventType.startsWith("api_")) return false;
  return asRecord(event.metadata)?.phase === "failure";
}

export function formatAuditMetadataString(metadata: Record<string, unknown> | undefined): string {
  if (!metadata || Object.keys(metadata).length === 0) return "";
  return Object.entries(metadata)
    .map(([k, v]) => `${k}: ${v}`)
    .join(", ");
}

function getWorkflowStepGroupKey(event: TraceAuditEventLike): string | null {
  const stepId = getStepIdFromMetadata(event.metadata);
  if (!stepId) return null;
  const sequence = getSequenceFromMetadata(event.metadata);
  return sequence != null ? `${sequence}:${stepId}` : stepId;
}

export function isWorkflowLifecycleAuditEvent(event: Pick<TraceAuditEventLike, "eventType" | "metadata">): boolean {
  if (event.eventType.startsWith("workflow_")) return true;
  const stage = getStageFromMetadata(event.metadata);
  const status = getStatusFromMetadata(event.metadata);
  return stage === "node" && (status === "started" || status === "completed");
}

/** Hide noisy workflow started/completed rows when a humanSummary sibling exists for the same step. */
export function filterRedundantWorkflowAuditEvents(events: TraceAuditEventLike[]): TraceAuditEventLike[] {
  const stepsWithHumanSummary = new Set<string>();
  for (const event of events) {
    if (!getAuditHumanSummary(event)) continue;
    const key = getWorkflowStepGroupKey(event);
    if (key) stepsWithHumanSummary.add(key);
  }
  if (stepsWithHumanSummary.size === 0) return events;
  return events.filter((event) => {
    if (getAuditHumanSummary(event)) return true;
    if (!isWorkflowLifecycleAuditEvent(event)) return true;
    const key = getWorkflowStepGroupKey(event);
    if (!key) return true;
    return !stepsWithHumanSummary.has(key);
  });
}

/**
 * B-VISION-A AC-8: Per-tier attempt payload carried on `tier_decision` workflow rows.
 * Mirrors `TierAttempt` from `server/src/application/vision-agent/vision-tier-types.ts`
 * but typed loosely so the UI tolerates additions without breaking.
 */
export interface TierDecisionAttempt {
  tier: string;
  outcome: "success" | "skip" | "fail";
  attemptIndex?: number;
  durationMs?: number;
  reasonCode?: string;
  executionPath?: string;
}

/** Returns the parsed `attempt` block when the audit event represents a `tier_decision` row. */
export function getTierDecisionAttempt(metadata: unknown): TierDecisionAttempt | null {
  const md = asRecord(metadata);
  if (!md) return null;
  const step = md.step ?? md.stepId;
  if (step !== "tier_decision") return null;
  const attempt = asRecord(md.attempt);
  if (!attempt) return null;
  const tier = typeof attempt.tier === "string" && attempt.tier.trim() !== "" ? attempt.tier : null;
  const outcomeValue = attempt.outcome;
  const outcome =
    outcomeValue === "success" || outcomeValue === "skip" || outcomeValue === "fail"
      ? outcomeValue
      : null;
  if (!tier || !outcome) return null;
  return {
    tier,
    outcome,
    ...(typeof attempt.attemptIndex === "number" ? { attemptIndex: attempt.attemptIndex } : {}),
    ...(typeof attempt.durationMs === "number" ? { durationMs: attempt.durationMs } : {}),
    ...(typeof attempt.reasonCode === "string" && attempt.reasonCode.trim() !== ""
      ? { reasonCode: attempt.reasonCode }
      : {}),
    ...(typeof attempt.executionPath === "string" && attempt.executionPath.trim() !== ""
      ? { executionPath: attempt.executionPath }
      : {}),
  };
}

/** Returns `cascade_of: rootEventId` when present (B-VISION-A AC-6). */
export function getCascadeOfFromMetadata(metadata: unknown): string | null {
  const md = asRecord(metadata);
  if (!md) return null;
  const value = md.cascade_of ?? md.cascadeOf;
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function buildSemanticWorkflowKey(
  workflowType: WorkflowType | null,
  stage: WorkflowProgressPayloadLike["stage"] | null,
  status: WorkflowProgressPayloadLike["status"] | null,
  stepId: string | null,
  sequence: number | null,
): string | null {
  if (!workflowType || !stage || !status || !stepId) return null;
  const sequencePart = sequence == null ? "none" : String(sequence);
  return `${workflowType}|${stage}|${status}|${stepId}|${sequencePart}`;
}

export function compareBySequenceTimestampAndId(
  a: { sequence?: number | null; timestamp: string; eventId: string },
  b: { sequence?: number | null; timestamp: string; eventId: string },
): number {
  const leftSequence = toFiniteNumber(a.sequence);
  const rightSequence = toFiniteNumber(b.sequence);
  if (leftSequence != null && rightSequence != null && leftSequence !== rightSequence) {
    return leftSequence - rightSequence;
  }
  if (leftSequence != null && rightSequence == null) return -1;
  if (leftSequence == null && rightSequence != null) return 1;

  const leftTimestamp = new Date(a.timestamp).getTime();
  const rightTimestamp = new Date(b.timestamp).getTime();
  if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp;
  return a.eventId.localeCompare(b.eventId);
}

export function inferWorkflowTypeFromEvents(
  mergedEvents: TraceAuditEventLike[],
  liveEvents: WorkflowProgressPayloadLike[],
): WorkflowType {
  const counts = new Map<WorkflowType, number>();
  const increment = (value: WorkflowType | null) => {
    if (!value) return;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  };

  for (const event of mergedEvents) {
    increment(getWorkflowTypeFromMetadata(event.metadata));
  }
  for (const event of liveEvents) {
    increment(event.workflowType ?? null);
  }

  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  if (sorted.length > 0) return sorted[0]![0];
  return "vision_goal_runner";
}

export function shouldRetainWorkflowEvent(
  event: WorkflowProgressPayloadLike | null | undefined,
  loadedRunId: string | null,
): event is WorkflowProgressPayloadLike {
  if (!event || event.type !== "agent_workflow_progress") return false;
  if (!event.eventId || !event.agentRunId) return false;
  if (!loadedRunId) return false;
  return event.agentRunId === loadedRunId;
}

export function appendWorkflowProgressEvent(
  previousEvents: WorkflowProgressPayloadLike[],
  incomingEvent: WorkflowProgressPayloadLike | null | undefined,
  loadedRunId: string | null,
  seenWorkflowEventIds: Set<string>,
  maxEvents = 1000,
): WorkflowProgressPayloadLike[] {
  if (!shouldRetainWorkflowEvent(incomingEvent, loadedRunId)) return previousEvents;
  if (seenWorkflowEventIds.has(incomingEvent.eventId)) return previousEvents;

  seenWorkflowEventIds.add(incomingEvent.eventId);
  const next = [...previousEvents, incomingEvent].sort((a, b) =>
    compareBySequenceTimestampAndId(
      {
        sequence: toFiniteNumber(a.sequence ?? a.metadata?.sequence),
        timestamp: a.timestamp,
        eventId: a.eventId,
      },
      {
        sequence: toFiniteNumber(b.sequence ?? b.metadata?.sequence),
        timestamp: b.timestamp,
        eventId: b.eventId,
      },
    ),
  );
  return next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
}

export function buildWorkflowTopologyState(
  mergedEvents: TraceAuditEventLike[],
  liveEvents: WorkflowProgressPayloadLike[],
): {
  workflowPath: readonly string[];
  latestStatusByStep: Map<string, WorkflowProgressPayloadLike["status"]>;
} {
  const inferredWorkflowType = inferWorkflowTypeFromEvents(mergedEvents, liveEvents);
  const workflowPath =
    inferredWorkflowType === "langgraph_orchestrator"
      ? ORCHESTRATOR_WORKFLOW_PATH
      : VISION_WORKFLOW_PATH;
  const latestStatusByStep = new Map<string, WorkflowProgressPayloadLike["status"]>();
  const workflowStatusEvents = mergedEvents
    .map((event) => {
      const stepId = getStepIdFromMetadata(event.metadata);
      const status = getStatusFromMetadata(event.metadata);
      const sequence = getSequenceFromMetadata(event.metadata);
      if (!stepId || !status) return null;
      return { stepId, status, sequence, timestamp: event.timestamp, eventId: event.eventId };
    })
    .filter(
      (
        event,
      ): event is {
        stepId: string;
        status: WorkflowProgressPayloadLike["status"];
        sequence: number | null;
        timestamp: string;
        eventId: string;
      } => Boolean(event),
    )
    .sort((a, b) =>
      compareBySequenceTimestampAndId(
        { sequence: a.sequence, timestamp: a.timestamp, eventId: a.eventId },
        { sequence: b.sequence, timestamp: b.timestamp, eventId: b.eventId },
      ),
    );

  for (const event of workflowStatusEvents) {
    latestStatusByStep.set(event.stepId, event.status);
  }

  return { workflowPath, latestStatusByStep };
}

export function mergeAuditWithLiveWorkflowEvents(
  baseEvents: TraceAuditEventLike[],
  liveEvents: WorkflowProgressPayloadLike[],
): TraceAuditEventLike[] {
  if (!liveEvents.length) return baseEvents;
  const existingIds = new Set(baseEvents.map((event) => event.eventId));
  const semanticKeys = new Set<string>();

  for (const event of baseEvents) {
    const semanticKey = buildSemanticWorkflowKey(
      getWorkflowTypeFromMetadata(event.metadata),
      getStageFromMetadata(event.metadata),
      getStatusFromMetadata(event.metadata),
      getStepIdFromMetadata(event.metadata),
      getSequenceFromMetadata(event.metadata),
    );
    if (semanticKey) semanticKeys.add(semanticKey);
  }

  const mapped: TraceAuditEventLike[] = [];
  for (const event of liveEvents) {
    if (existingIds.has(event.eventId)) continue;
    const sequence = toFiniteNumber(event.sequence ?? event.metadata?.sequence);
    const semanticKey = buildSemanticWorkflowKey(
      event.workflowType,
      event.stage,
      event.status,
      event.stepId ?? null,
      sequence,
    );
    if (semanticKey && semanticKeys.has(semanticKey)) continue;
    if (semanticKey) semanticKeys.add(semanticKey);
    mapped.push({
      eventId: event.eventId,
      agentRunId: event.agentRunId,
      timestamp: event.timestamp,
      eventType: `workflow_${event.stage}_${event.status}`,
      studioId: event.studioId,
      platform: event.platform,
      actor: event.actor,
      traceId: "",
      spanId: "",
      metadata: {
        workflowType: event.workflowType,
        stage: event.stage,
        status: event.status,
        stepId: event.stepId,
        label: event.label ?? event.stepId,
        sequence,
        ...(event.metadata ?? {}),
      },
    });
  }

  if (!mapped.length) return baseEvents;
  return [...baseEvents, ...mapped].sort((a, b) =>
    compareBySequenceTimestampAndId(
      { sequence: getSequenceFromMetadata(a.metadata), timestamp: a.timestamp, eventId: a.eventId },
      { sequence: getSequenceFromMetadata(b.metadata), timestamp: b.timestamp, eventId: b.eventId },
    ),
  );
}

/** `[REDACTED:content sha256=… bytes=N]` markers persisted by the privacy scrubber. */
const REDACTED_CONTENT_MARKER_RE = /\[REDACTED:content[^\]]*\]/gi;
/** Legacy compose summaries carried the raw draft UUID inline — strip for display. */
const DRAFT_ID_SENTENCE_RE =
  /\s*Draft id:\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.?/i;

/**
 * Operator-facing projection of a decision step: the summary keeps only readable
 * prose (privacy markers replaced, inline draft UUID removed); everything raw
 * (UUID lists, hashes, enum paths) moves to `technicalDetail` for an expandable lane.
 */
export function decisionStepDisplay(
  step: Pick<OrchestratorDecisionStepLike, "summary" | "detail">,
  redactedLabel: string,
): { summary: string; technicalDetail: string | null } {
  const rawSummary = step.summary ?? "";
  const draftIdMatch = rawSummary.match(DRAFT_ID_SENTENCE_RE);
  const summary = rawSummary
    .replace(DRAFT_ID_SENTENCE_RE, "")
    .replace(REDACTED_CONTENT_MARKER_RE, redactedLabel)
    .replace(/\s{2,}/g, " ")
    .trim();
  const technicalParts = [
    draftIdMatch ? draftIdMatch[0].trim().replace(/\.$/, "") : null,
    step.detail?.trim() || null,
  ].filter(Boolean) as string[];
  return {
    summary,
    technicalDetail: technicalParts.length > 0 ? technicalParts.join(" · ") : null,
  };
}

/** Orchestrator / vision trace detail lines — truncate for timeline display. */
export function truncateTraceDetailText(text: string, max = 320): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

export function shortTraceHash(hash: string | null | undefined): string {
  if (!hash?.trim()) return "";
  const h = hash.trim();
  return h.length <= 12 ? h : `${h.slice(0, 8)}…${h.slice(-4)}`;
}

export type OrchestratorTraceLlmCallLike = {
  operation: string;
  model: string;
  promptKey: string;
  temperature: number;
  maxTokens?: number | null;
  promptKind: "tool" | "messages";
  promptHash?: string | null;
  promptSummary?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  totalTokens?: number | null;
  costUsd?: number | null;
  confidence?: number | null;
  intent?: string | null;
  reasoningExcerpt?: string | null;
};

export function orchestratorLlmCallsByOperation(
  calls: OrchestratorTraceLlmCallLike[] | null | undefined,
): Map<string, OrchestratorTraceLlmCallLike> {
  const map = new Map<string, OrchestratorTraceLlmCallLike>();
  for (const call of calls ?? []) {
    if (call.operation) map.set(call.operation, call);
  }
  return map;
}

/**
 * C13: flatten a tool's outcome flag map into stable "key: value" chip strings for the
 * trace panel. Values are machine enums/booleans/numbers (never translated, never
 * free text — the server-side allowlist guarantees that); deterministic key order.
 */
export function formatToolOutcomeEntries(
  outcome: Record<string, boolean | number | string> | null | undefined,
): string[] {
  if (!outcome || typeof outcome !== "object") return [];
  return Object.keys(outcome)
    .sort()
    .filter((key) => {
      const v = outcome[key];
      return (
        typeof v === "boolean" ||
        (typeof v === "number" && Number.isFinite(v)) ||
        typeof v === "string"
      );
    })
    .map((key) => `${key}: ${String(outcome[key])}`);
}

export type OrchestratorDecisionStepLike = {
  id: string;
  phase: string;
  title: string;
  summary: string;
  certainty?: number | null;
  certaintyLabel?: string | null;
  detail?: string | null;
};

export type OrchestratorTraceBlockLike = {
  id: string;
  /** Conversation behind this run — used for the "Open conversation" deep link. */
  conversationId: string;
  intent: string | null;
  confidence: number | null;
  escalationReason: string | null;
  /**
   * `outcome` = the trace's deterministic non-PII per-tool outcome flags (closed
   * allowlist, e.g. identityMatchStatus / matchCount / identityCandidateCount /
   * externalCustomerId / booked). Rendered as key:value chips so the machine verdict
   * survives even when the scrubbed 200-char summary truncates it.
   */
  toolsCalled: Array<{
    tool: string;
    success: boolean;
    summary?: string;
    outcome?: Record<string, boolean | number | string>;
  }>;
  ragChunkTitles: string[];
  promptKeys: { classify?: string; compose?: string } | null;
  promptHashes: Record<string, string> | null;
  modelId: string | null;
  decisionSteps: OrchestratorDecisionStepLike[];
  llmCalls: OrchestratorTraceLlmCallLike[];
  decisionOrigin: string | null;
  groundingVerdict: string | null;
};

